import type { BusStop, Coordinate, RouteSnapshot, VehiclePosition } from "@bus/shared";

export interface VehicleRouteProgress {
  vehicle: VehiclePosition;
  segmentIndex: number;
  progress: number;
  arriving: boolean;
}

const distanceScore = (a: Coordinate, b: Coordinate) => {
  const latitudeScale = Math.cos(((a.latitude + b.latitude) / 2) * Math.PI / 180);
  return ((a.longitude - b.longitude) * latitudeScale) ** 2 + (a.latitude - b.latitude) ** 2;
};

const closestIndex = (points: Coordinate[], target: Coordinate, from = 0) => {
  let bestIndex = Math.min(from, Math.max(0, points.length - 1));
  let bestScore = Number.POSITIVE_INFINITY;
  for (let index = bestIndex; index < points.length; index += 1) {
    const score = distanceScore(points[index]!, target);
    if (score < bestScore) { bestScore = score; bestIndex = index; }
  }
  return bestIndex;
};

export function vehicleRouteProgress(snapshot: Pick<RouteSnapshot, "shape" | "stops" | "vehicles">): VehicleRouteProgress[] {
  if (snapshot.stops.length < 2 || snapshot.shape.length < 2) return [];
  let previousShapeIndex = 0;
  const stopShapeIndexes = snapshot.stops.map((stop) => {
    const index = closestIndex(snapshot.shape, stop.position, previousShapeIndex);
    previousShapeIndex = index;
    return index;
  });

  return snapshot.vehicles.map((vehicle) => {
    const shapeIndex = closestIndex(snapshot.shape, vehicle.position);
    let segmentIndex = stopShapeIndexes.findIndex((nextIndex, index) => index > 0 && shapeIndex <= nextIndex) - 1;
    if (segmentIndex < 0) segmentIndex = shapeIndex <= stopShapeIndexes[0]! ? 0 : snapshot.stops.length - 2;
    const start = stopShapeIndexes[segmentIndex]!;
    const end = stopShapeIndexes[segmentIndex + 1]!;
    const progress = end === start ? 0.5 : Math.max(0, Math.min(1, (shapeIndex - start) / (end - start)));
    return { vehicle, segmentIndex, progress, arriving: progress >= 0.82 };
  });
}

export function nearestStop(stops: BusStop[], location: Coordinate): { stopId: string; distanceMeters: number } | null {
  let nearest: { stopId: string; distanceMeters: number } | null = null;
  for (const stop of stops) {
    const rad = Math.PI / 180;
    const dLat = (stop.position.latitude - location.latitude) * rad;
    const dLon = (stop.position.longitude - location.longitude) * rad;
    const lat1 = location.latitude * rad;
    const lat2 = stop.position.latitude * rad;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    const distanceMeters = Math.round(6_371_000 * 2 * Math.asin(Math.sqrt(h)));
    if (!nearest || distanceMeters < nearest.distanceMeters) nearest = { stopId: stop.id, distanceMeters };
  }
  return nearest;
}
